import { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import Modal, { field, lbl } from "./Modal";
import ImageUploader from "./ImageUploader";
import { Pencil, Trash2, Star } from "lucide-react";

const EMPTY = {
  name: "", slug: "", lineage: "", type: "hybrid", terpenes: [], flavor_notes: [],
  description: "", images: [], coa_url: "", status: "AVAILABLE", featured: false,
  meta_title: "", meta_description: "",
};

export default function StrainsTab() {
  const [strains, setStrains] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/strains").then(({ data }) => setStrains(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s });
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCsv = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }));

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) await client.put(`/admin/strains/${editing.id}`, payload);
      else await client.post("/admin/strains", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    await client.delete(`/admin/strains/${s.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl">STRAINS <span className="text-yaza-accent">{strains.length}</span></h2>
        <button onClick={openNew} data-testid="new-strain-btn" className="rounded-none bg-yaza-accent px-5 py-2 text-xs font-bold uppercase tracking-widest text-black hover:bg-yaza-accentHover">+ New Strain</button>
      </div>

      <div className="overflow-hidden rounded-lg border border-yaza-border">
        {strains.map((s) => (
          <div key={s.id} className="flex items-center gap-4 border-b border-yaza-border px-4 py-3 last:border-0" data-testid={`admin-strain-${s.slug}`}>
            <img src={s.images?.[0]} alt="" className="h-12 w-12 rounded object-cover bg-yaza-surface2" />
            <div className="flex-1">
              <p className="flex items-center gap-2 font-semibold">{s.name} {s.featured && <Star size={13} className="text-yaza-accent" fill="#ff2a6d" />}</p>
              <p className="text-xs text-neutral-500">{s.type} · {s.status}</p>
            </div>
            <button onClick={() => openEdit(s)} data-testid={`edit-strain-${s.slug}`} className="text-neutral-400 hover:text-white"><Pencil size={16} /></button>
            <button onClick={() => del(s)} data-testid={`delete-strain-${s.slug}`} className="text-neutral-400 hover:text-yaza-accent"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Strain" : "New Strain"} wide>
        <div className="grid gap-4 md:grid-cols-2">
          <div><label className={lbl}>Name</label><input className={field} value={form.name} onChange={set("name")} data-testid="strain-name" /></div>
          <div><label className={lbl}>Slug (optional)</label><input className={field} value={form.slug || ""} onChange={set("slug")} placeholder="auto from name" /></div>
          <div><label className={lbl}>Lineage / Cross</label><input className={field} value={form.lineage} onChange={set("lineage")} /></div>
          <div>
            <label className={lbl}>Type</label>
            <select className={field} value={form.type} onChange={set("type")} data-testid="strain-type">
              <option value="indica">Indica</option><option value="sativa">Sativa</option><option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Availability</label>
            <select className={field} value={form.status} onChange={set("status")} data-testid="strain-status">
              <option value="AVAILABLE">Available</option><option value="LIMITED">Limited</option>
              <option value="DROPPING_SOON">Dropping Soon</option><option value="SOLD_OUT">Sold Out</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="strain-featured" />
            <label className="text-sm">Featured</label>
          </div>
          <div className="md:col-span-2"><label className={lbl}>Terpenes (comma separated)</label><input className={field} value={form.terpenes.join(", ")} onChange={setCsv("terpenes")} /></div>
          <div className="md:col-span-2"><label className={lbl}>Flavor Notes (comma separated)</label><input className={field} value={form.flavor_notes.join(", ")} onChange={setCsv("flavor_notes")} /></div>
          <div className="md:col-span-2"><label className={lbl}>Description (HTML allowed)</label><textarea className={field} rows={4} value={form.description} onChange={set("description")} /></div>
          <div className="md:col-span-2"><label className={lbl}>Images</label><ImageUploader images={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} /></div>
          <div className="md:col-span-2"><label className={lbl}>COA / Lab URL</label><input className={field} value={form.coa_url} onChange={set("coa_url")} /></div>
          <div><label className={lbl}>Meta Title</label><input className={field} value={form.meta_title} onChange={set("meta_title")} /></div>
          <div><label className={lbl}>Meta Description</label><input className={field} value={form.meta_description} onChange={set("meta_description")} /></div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-strain" className="mt-6 w-full rounded-none bg-yaza-accent py-3 text-sm font-bold uppercase tracking-widest text-black hover:bg-yaza-accentHover disabled:opacity-50">{saving ? "Saving…" : "Save Strain"}</button>
      </Modal>
    </div>
  );
}
